import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Tooltip } from '../components/ui/Tooltip';
import { Plus, Filter, Download, Eye, Edit, Search, GraduationCap, FileText, Users, Award, X, Save, CheckCircle2, Building2, Trash2, AlertCircle, UserPlus, UserCheck } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
import { mockData } from '../services/mockData';
// Realistic Italian course names
const activeCourses = [{
  id: 1,
  code: 'CRS-001',
  title: 'Corso RSPP Modulo A',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2024-05-20',
  enrolled: 12,
  status: 'In corso',
  instructor: 'Ing. Rossi',
  location: 'Sede SEF',
  commessa: 'SEFORM',
  participants: []
}, {
  id: 2,
  code: 'CRS-002',
  title: 'HACCP Responsabile Industria Alimentare',
  type: 'HACCP',
  mode: 'Sede cliente',
  date: '2024-05-22',
  enrolled: 8,
  status: 'In corso',
  instructor: 'Dott. Bianchi',
  location: 'Azienda 1 SRL',
  commessa: 'Privata',
  committente: 'Azienda 0 SRL',
  participants: []
}, {
  id: 3,
  code: 'CRS-003',
  title: 'Antincendio Rischio Medio',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2024-05-25',
  enrolled: 15,
  status: 'In corso',
  instructor: 'P.I. Verdi',
  location: 'Sede SEF',
  commessa: 'SEFORM',
  participants: []
}, {
  id: 4,
  code: 'CRS-004',
  title: 'Primo Soccorso Gruppo B',
  type: 'Sicurezza',
  mode: 'A distanza',
  date: '2024-05-28',
  enrolled: 20,
  status: 'In corso',
  instructor: 'Dott. Neri',
  location: 'Online',
  commessa: 'SEFORM',
  participants: []
}, {
  id: 5,
  code: 'CRS-005',
  title: 'Formazione Lavoratori Rischio Alto',
  type: 'Sicurezza',
  mode: 'Sede cliente',
  date: '2024-06-01',
  enrolled: 10,
  status: 'In corso',
  instructor: 'Ing. Gialli',
  location: 'Azienda 2 SRL',
  commessa: 'Privata',
  committente: 'Azienda 0 SRL',
  participants: []
}, {
  id: 6,
  code: 'CRS-006',
  title: 'Aggiornamento RLS',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2024-06-05',
  enrolled: 18,
  status: 'In corso',
  instructor: 'Avv. Blu',
  location: 'Sede SEF',
  commessa: 'SEFORM',
  participants: []
}, {
  id: 7,
  code: 'CRS-007',
  title: 'HACCP Addetti Manipolazione',
  type: 'HACCP',
  mode: 'A distanza',
  date: '2024-06-10',
  enrolled: 25,
  status: 'In corso',
  instructor: 'Dott. Viola',
  location: 'Online',
  commessa: 'SEFORM',
  participants: []
}, {
  id: 8,
  code: 'CRS-008',
  title: 'Preposto Aggiornamento',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2024-06-15',
  enrolled: 14,
  status: 'In corso',
  instructor: 'Ing. Arancio',
  location: 'Sede SEF',
  commessa: 'SEFORM',
  participants: []
}, {
  id: 9,
  code: 'CRS-009',
  title: 'Lavori in Quota',
  type: 'Sicurezza',
  mode: 'Sede cliente',
  date: '2024-06-20',
  enrolled: 6,
  status: 'In corso',
  instructor: 'Geom. Marroni',
  location: 'Cantiere',
  commessa: 'Privata',
  committente: 'Azienda 0 SRL',
  participants: []
}, {
  id: 10,
  code: 'CRS-010',
  title: 'Privacy GDPR Base',
  type: 'Privacy',
  mode: 'A distanza',
  date: '2024-06-25',
  enrolled: 30,
  status: 'In corso',
  instructor: 'Avv. Rosa',
  location: 'Online',
  commessa: 'SEFORM',
  participants: []
}];
const scheduledCourses = Array.from({
  length: 10
}, (_, i) => ({
  id: 11 + i,
  code: `CRS-0${11 + i}`,
  title: ['Corso PES/PAV', 'Mulettisti', 'Spazi Confinati', 'Aggiornamento Primo Soccorso', 'Dirigenti', 'RSPP Modulo B', 'RSPP Modulo C', 'Formatore Sicurezza', 'HACCP Base', 'Privacy Avanzato'][i],
  type: i % 2 === 0 ? 'Sicurezza' : 'Altro',
  mode: ['Sede SEF', 'Sede cliente', 'A distanza'][i % 3],
  date: '2024-09-01',
  enrolled: 0,
  status: 'Programmato',
  instructor: 'TBD',
  location: 'TBD',
  commessa: i % 3 === 0 ? 'Privata' : 'SEFORM',
  committente: i % 3 === 0 ? 'Azienda 0 SRL' : undefined,
  participants: []
}));
const archiveCourses = Array.from({
  length: 15
}, (_, i) => ({
  id: 21 + i,
  code: `CRS-0${21 + i}`,
  title: `Corso Archivio ${i + 1}`,
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2023-12-01',
  enrolled: 15,
  status: 'Completato',
  instructor: 'Ing. Vecchio',
  location: 'Sede SEF',
  commessa: 'SEFORM',
  participants: []
}));
const allCoursesData = [...activeCourses, ...scheduledCourses, ...archiveCourses];
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'title',
  label: 'Titolo'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'mode',
  label: 'Modalità'
}, {
  key: 'date',
  label: 'Data'
}, {
  key: 'enrolled',
  label: 'Iscritti'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'commessa',
  label: 'Commessa'
}, {
  key: 'actions',
  label: 'Azioni'
}];
type ViewType = 'active' | 'scheduled' | 'archive';
export function TrainingPage() {
  const [activeView, setActiveView] = useState<ViewType>('active');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isPracticeModalOpen, setIsPracticeModalOpen] = useState(false);
  const [isParticipantModalOpen, setIsParticipantModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  // Participant Modal State
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [enrolledWorkers, setEnrolledWorkers] = useState<any[]>([]);
  const [isPrivateCitizen, setIsPrivateCitizen] = useState(false);
  const [privateCitizenName, setPrivateCitizenName] = useState('');
  const {
    addNotification
  } = useNotifications();
  // Filter courses based on active view
  const getFilteredCourses = () => {
    switch (activeView) {
      case 'active':
        return activeCourses;
      case 'scheduled':
        return scheduledCourses;
      case 'archive':
        return archiveCourses;
      default:
        return [];
    }
  };
  const coursesData = getFilteredCourses();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(coursesData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['code', 'title']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, activeView]);
  // Update visible columns based on view
  useEffect(() => {
    if (activeView === 'active') {
      if (!visibleColumns.includes('commessa')) {
        setVisibleColumns([...visibleColumns, 'commessa']);
      }
    } else {
      setVisibleColumns(visibleColumns.filter(c => c !== 'commessa'));
    }
  }, [activeView]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleAddParticipants = (item: any) => {
    setSelectedItem(item);
    setEnrolledWorkers(item.participants || []);
    setIsPrivateCitizen(false);
    setPrivateCitizenName('');
    // If private course, pre-select company
    if (item.commessa === 'Privata' && item.committente) {
      const client = mockData.getClients().find(c => c.name === item.committente);
      if (client) setSelectedCompanyId(String(client.id));
    } else {
      setSelectedCompanyId('');
    }
    setIsParticipantModalOpen(true);
  };
  const handleCreateCourse = () => {
    setSelectedItem(null);
    const nextCode = `CRS-${String(allCoursesData.length + 1).padStart(3, '0')}`;
    setFormData({
      code: nextCode,
      status: 'Programmato'
    });
    setIsModalOpen(true);
  };
  const handleCreatePractice = () => {
    setFormData({
      status: 'In valutazione'
    });
    setIsPracticeModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
      addNotification('success', 'Eliminato', 'Corso eliminato con successo');
    }, 1500);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Corso salvato', 'Il corso è stato salvato con successo.');
    }, 1500);
  };
  const handleSavePractice = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsPracticeModalOpen(false);
      addNotification('success', 'Pratica iscrizione creata', 'La pratica è stata creata con stato "In valutazione".');
    }, 1500);
  };
  const handleEnrollWorker = (worker: any) => {
    if (enrolledWorkers.some(w => w.id === worker.id)) return;
    const newParticipant = {
      id: worker.id,
      name: worker.name,
      company: worker.company,
      status: 'Iscritto'
    };
    setEnrolledWorkers([...enrolledWorkers, newParticipant]);
  };
  const handleUnenrollWorker = (workerId: number) => {
    setEnrolledWorkers(enrolledWorkers.filter(w => w.id !== workerId));
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  const viewTabs = [{
    id: 'active' as ViewType,
    label: 'Corsi attivi',
    count: activeCourses.length
  }, {
    id: 'scheduled' as ViewType,
    label: 'In programma',
    count: scheduledCourses.length
  }, {
    id: 'archive' as ViewType,
    label: 'Archivio corsi',
    count: archiveCourses.length
  }];
  // Determine data to display (paginated only for archive)
  const displayData = activeView === 'archive' ? paginatedData : searchResult;
  return <Layout data-id="element-5011">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-5012">
        <div data-id="element-5013">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-5014">Formazione</h1>
          <p className="text-slate-500 mt-1" data-id="element-5015">
            Gestione completa corsi, iscrizioni e certificazioni.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-5016">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-5018" />} data-id="element-5017">
            Esporta
          </Button>
          <Button variant="flat" color="secondary" onClick={handleCreatePractice} startContent={<FileText className="h-4 w-4" data-id="element-5020" />} data-id="element-5019">
            Pratica iscrizione
          </Button>
          <Button onClick={handleCreateCourse} startContent={<Plus className="h-4 w-4" data-id="element-5022" />} data-id="element-5021">
            Nuovo corso
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="bg-slate-100 p-1 rounded-xl flex items-center w-fit mb-6" data-id="element-5023">
        {viewTabs.map(tab => <button key={tab.id} onClick={() => setActiveView(tab.id)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${activeView === tab.id ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5024">
            {tab.label}
            <Badge variant={activeView === tab.id ? 'primary' : 'neutral'} size="sm" data-id="element-5025">
              {tab.count}
            </Badge>
          </button>)}
      </div>

      <Card className="mb-6" noPadding data-id="element-5026">
        <div className="p-4 flex flex-col gap-4" data-id="element-5027">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-5028">
            <div className="flex-1" data-id="element-5029">
              <Input placeholder="Cerca corso..." startContent={<Search className="h-4 w-4" data-id="element-5031" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-5030" />
            </div>
            <div className="flex gap-2" data-id="element-5032">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-5034" />} data-id="element-5033">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-5035">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="training" lockedColumns={['code']} data-id="element-5036" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-5037">
              <Select label="Modalità" value={filters.mode || ''} onChange={e => setFilter('mode', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Sede cliente',
            label: 'Sede cliente'
          }, {
            value: 'Sede SEF',
            label: 'Sede SEF'
          }, {
            value: 'A distanza',
            label: 'A distanza'
          }]} data-id="element-5038" />
              <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Sicurezza',
            label: 'Sicurezza'
          }, {
            value: 'HACCP',
            label: 'HACCP'
          }]} data-id="element-5039" />
              <div className="col-span-full flex justify-end" data-id="element-5040">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-5042" />} data-id="element-5041">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-5043">
        <div className="overflow-x-auto" data-id="element-5044">
          <table className="w-full text-left border-collapse table-auto" data-id="element-5045">
            <thead data-id="element-5046">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-5047">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-5048">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-5049">
              {displayData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-5050">
                  {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-5051">
                      {item.code}
                    </td>}
                  {visibleColumns.includes('title') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-5052">
                      {item.title}
                    </td>}
                  {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-5053">
                      {item.type}
                    </td>}
                  {visibleColumns.includes('mode') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-5054">
                      {item.mode}
                    </td>}
                  {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-5055">
                      {item.date}
                    </td>}
                  {visibleColumns.includes('enrolled') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-5056">
                      {item.enrolled}
                    </td>}
                  {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-5057">
                      <Badge variant={item.status === 'In corso' ? 'success' : item.status === 'Programmato' ? 'info' : 'neutral'} data-id="element-5058">
                        {item.status}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('commessa') && <td className="px-4 py-4" data-id="element-5059">
                      <Badge variant={item.commessa === 'SEFORM' ? 'primary' : 'neutral'} data-id="element-5060">
                        {item.commessa || '-'}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-5061">
                      <div className="flex items-center gap-1" data-id="element-5062">
                        <Tooltip content="Visualizza corso" data-id="element-5063">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-5064">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-5065" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Aggiungi partecipanti" data-id="element-5066">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleAddParticipants(item)} data-id="element-5067">
                            <UserPlus className="h-4 w-4 text-blue-500" data-id="element-5068" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Modifica corso" data-id="element-5069">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-5070">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-5071" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Elimina corso" data-id="element-5072">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-5073">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-5074" />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        {activeView === 'archive' && <div className="border-t border-slate-100" data-id="element-5075">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-5076" />
          </div>}
      </Card>

      {/* View Course Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio corso" size="2xl" data-id="element-5077">
        {selectedItem && <div className="space-y-6" data-id="element-5078">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-5079">
              <div data-id="element-5080">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5081">
                  Codice
                </span>
                <p className="font-mono font-medium" data-id="element-5082">{selectedItem.code}</p>
              </div>
              <div data-id="element-5083">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5084">
                  Stato
                </span>
                <div className="mt-1" data-id="element-5085">
                  <Badge variant={selectedItem.status === 'In corso' ? 'success' : selectedItem.status === 'Programmato' ? 'info' : 'neutral'} data-id="element-5086">
                    {selectedItem.status}
                  </Badge>
                </div>
              </div>
              <div data-id="element-5087">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5088">
                  Modalità
                </span>
                <p className="font-medium" data-id="element-5089">{selectedItem.mode}</p>
              </div>
              <div className="col-span-full" data-id="element-5090">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5091">
                  Titolo
                </span>
                <p className="font-medium text-lg" data-id="element-5092">{selectedItem.title}</p>
              </div>
              <div data-id="element-5093">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5094">
                  Tipologia
                </span>
                <p className="font-medium" data-id="element-5095">{selectedItem.type}</p>
              </div>
              <div data-id="element-5096">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5097">
                  Data
                </span>
                <p className="font-medium" data-id="element-5098">{selectedItem.date}</p>
              </div>
              <div data-id="element-5099">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5100">
                  Docente
                </span>
                <p className="font-medium" data-id="element-5101">{selectedItem.instructor}</p>
              </div>
              <div data-id="element-5102">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5103">
                  Sede
                </span>
                <p className="font-medium" data-id="element-5104">{selectedItem.location}</p>
              </div>
              {selectedItem.commessa && <div data-id="element-5105">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5106">
                    Commessa
                  </span>
                  <div className="mt-1" data-id="element-5107">
                    <Badge variant={selectedItem.commessa === 'SEFORM' ? 'primary' : 'neutral'} data-id="element-5108">
                      {selectedItem.commessa}
                    </Badge>
                  </div>
                </div>}
              {selectedItem.committente && <div className="col-span-2" data-id="element-5109">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5110">
                    Azienda committente
                  </span>
                  <p className="font-medium" data-id="element-5111">{selectedItem.committente}</p>
                </div>}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-5112">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-5113">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-5115" />} data-id="element-5114">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Edit Course Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica corso' : 'Nuovo corso'} size="2xl" data-id="element-5116">
        <AnimatePresence mode="wait" data-id="element-5117">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-5118">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-5119">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-5120" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-5121">
                Corso salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-5122">
              <div className="space-y-6" data-id="element-5123">
                <section data-id="element-5124">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5125">
                    <GraduationCap className="h-4 w-4" data-id="element-5126" /> Dettagli corso
                  </h4>

                  {/* Commessa Section - Upfront */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-6" data-id="element-5127">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5128">
                      <Select label="Commessa" value={formData.commessa || 'SEFORM'} onChange={e => setFormData({
                    ...formData,
                    commessa: e.target.value
                  })} options={[{
                    value: 'SEFORM',
                    label: 'SEFORM'
                  }, {
                    value: 'Privata',
                    label: 'Privata'
                  }]} required className="font-medium" data-id="element-5129" />
                      {formData.commessa === 'Privata' && <Select label="Azienda committente" value={formData.committente || ''} onChange={e => setFormData({
                    ...formData,
                    committente: e.target.value
                  })} options={mockData.getClients().map(c => ({
                    value: c.name,
                    label: c.name
                  }))} required data-id="element-5130" />}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5131">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} disabled={!!selectedItem} data-id="element-5132" />
                    <Select label="Stato" value={formData.status || 'Programmato'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Programmato',
                  label: 'Programmato'
                }, {
                  value: 'In corso',
                  label: 'In corso'
                }, {
                  value: 'Completato',
                  label: 'Completato'
                }]} required data-id="element-5133" />
                    <Input label="Titolo" value={formData.title || ''} onChange={e => setFormData({
                  ...formData,
                  title: e.target.value
                })} placeholder="" required className="col-span-full" data-id="element-5134" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Sicurezza',
                  label: 'Sicurezza'
                }, {
                  value: 'HACCP',
                  label: 'HACCP'
                }]} required data-id="element-5135" />
                    <Select label="Modalità" value={formData.mode || ''} onChange={e => setFormData({
                  ...formData,
                  mode: e.target.value
                })} options={[{
                  value: 'Sede cliente',
                  label: 'Sede cliente'
                }, {
                  value: 'Sede SEF',
                  label: 'Sede SEF'
                }, {
                  value: 'A distanza',
                  label: 'A distanza'
                }]} required data-id="element-5136" />
                    <DatePicker label="Data" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-5137" />
                    <Input label="Docente" value={formData.instructor || ''} onChange={e => setFormData({
                  ...formData,
                  instructor: e.target.value
                })} placeholder="" data-id="element-5138" />
                    <Input label="Sede" value={formData.location || ''} onChange={e => setFormData({
                  ...formData,
                  location: e.target.value
                })} placeholder="SEF Via Calamandrei Arezzo" data-id="element-5139" />
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-5140">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-5141">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-5143" />} data-id="element-5142">
                    Salva corso
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Participant Management Modal */}
      <Modal isOpen={isParticipantModalOpen} onClose={() => setIsParticipantModalOpen(false)} title={`Aggiungi partecipanti — ${selectedItem?.title}`} size="2xl" data-id="element-5144">
        <div className="space-y-6 flex flex-col h-[70vh]" data-id="element-5145">
          {/* Private Citizen Toggle + Company Selection */}
          <div className="flex-shrink-0 space-y-4" data-id="element-5146">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-5147">
              <input type="checkbox" checked={isPrivateCitizen} onChange={e => {
              setIsPrivateCitizen(e.target.checked);
              if (e.target.checked) {
                setSelectedCompanyId('');
              }
              setPrivateCitizenName('');
            }} className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange h-4 w-4" data-id="element-5148" />
              <span className="text-sm font-medium text-slate-700" data-id="element-5149">
                Privato cittadino
              </span>
            </label>

            {isPrivateCitizen ? <Input label="Nome e Cognome" placeholder="Inserisci nome e cognome" value={privateCitizenName} onChange={e => setPrivateCitizenName(e.target.value)} fullWidth required data-id="element-5150" /> : <Select label="Seleziona Azienda" value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)} options={[{
            value: '',
            label: "Seleziona un'azienda..."
          }, ...mockData.getClients().map(c => ({
            value: String(c.id),
            label: c.name
          }))]} disabled={selectedItem?.commessa === 'Privata'} description={selectedItem?.commessa === 'Privata' ? 'Corso privato: azienda bloccata al committente' : undefined} data-id="element-5151" />}
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar space-y-6 pr-2" data-id="element-5152">
            {/* Enrolled Participants */}
            <section data-id="element-5153">
              <div className="flex justify-between items-center mb-3" data-id="element-5154">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider flex items-center gap-2" data-id="element-5155">
                  <Users className="h-4 w-4" data-id="element-5156" /> Partecipanti iscritti (
                  {enrolledWorkers.length})
                </h4>
                {enrolledWorkers.length > 0 && <Button size="sm" variant="ghost" color="danger" onClick={() => setEnrolledWorkers([])} data-id="element-5157">
                    Disiscrivi tutti
                  </Button>}
              </div>
              <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-5158">
                <table className="w-full text-left text-sm" data-id="element-5159">
                  <thead className="bg-slate-50 text-slate-500 font-semibold" data-id="element-5160">
                    <tr data-id="element-5161">
                      <th className="p-3" data-id="element-5162">Nome</th>
                      <th className="p-3" data-id="element-5163">Azienda</th>
                      <th className="p-3" data-id="element-5164">Stato</th>
                      <th className="p-3 text-right" data-id="element-5165">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-5166">
                    {enrolledWorkers.length === 0 ? <tr data-id="element-5167">
                        <td colSpan={4} className="p-6 text-center text-slate-500 italic" data-id="element-5168">
                          Nessun partecipante iscritto
                        </td>
                      </tr> : enrolledWorkers.map(p => <tr key={p.id} className="bg-green-50/30" data-id="element-5169">
                          <td className="p-3 font-medium" data-id="element-5170">{p.name}</td>
                          <td className="p-3 text-slate-600" data-id="element-5171">{p.company}</td>
                          <td className="p-3" data-id="element-5172">
                            <div className="flex items-center gap-1.5 text-green-600 font-medium" data-id="element-5173">
                              <CheckCircle2 className="h-4 w-4" data-id="element-5174" />
                              Iscritto
                            </div>
                          </td>
                          <td className="p-3 text-right" data-id="element-5175">
                            <Button size="sm" variant="ghost" color="danger" className="!px-2" onClick={() => handleUnenrollWorker(p.id)} data-id="element-5176">
                              <X className="h-4 w-4" data-id="element-5177" />
                            </Button>
                          </td>
                        </tr>)}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Available Workers */}
            {selectedCompanyId && <section data-id="element-5178">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-5179">
                  <UserPlus className="h-4 w-4" data-id="element-5180" /> Dipendenti disponibili —{' '}
                  {mockData.getClient(Number(selectedCompanyId))?.name}
                </h4>
                <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-5181">
                  <table className="w-full text-left text-sm" data-id="element-5182">
                    <thead className="bg-slate-50 text-slate-500 font-semibold" data-id="element-5183">
                      <tr data-id="element-5184">
                        <th className="p-3" data-id="element-5185">Nome</th>
                        <th className="p-3" data-id="element-5186">Mansione</th>
                        <th className="p-3" data-id="element-5187">Stato</th>
                        <th className="p-3 text-right" data-id="element-5188">Aggiungi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-5189">
                      {mockData.getWorkersByClient(Number(selectedCompanyId)).map(worker => {
                    const isEnrolled = enrolledWorkers.some(w => w.id === worker.id);
                    const hasCompleted = worker.id % 5 === 0; // Mock check for completed course
                    return <tr key={worker.id} className={isEnrolled ? 'bg-slate-50 opacity-75' : 'hover:bg-slate-50'} data-id="element-5190">
                              <td className="p-3 font-medium" data-id="element-5191">{worker.name}</td>
                              <td className="p-3 text-slate-600" data-id="element-5192">
                                {worker.role}
                              </td>
                              <td className="p-3" data-id="element-5193">
                                {hasCompleted && <Badge variant="success" size="sm" className="mr-2" data-id="element-5194">
                                    Corso sostenuto
                                  </Badge>}
                                {isEnrolled && <span className="text-slate-400 text-xs font-medium" data-id="element-5195">
                                    Già iscritto
                                  </span>}
                              </td>
                              <td className="p-3 text-right" data-id="element-5196">
                                <Button size="sm" variant={isEnrolled ? 'flat' : 'light'} color={isEnrolled ? 'success' : 'primary'} className="!px-2" disabled={isEnrolled} onClick={() => handleEnrollWorker(worker)} data-id="element-5197">
                                  {isEnrolled ? <CheckCircle2 className="h-4 w-4" data-id="element-5198" /> : <UserPlus className="h-4 w-4" data-id="element-5199" />}
                                </Button>
                              </td>
                            </tr>;
                  })}
                    </tbody>
                  </table>
                </div>
              </section>}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-auto flex-shrink-0" data-id="element-5200">
            <Button variant="flat" color="default" onClick={() => setIsParticipantModalOpen(false)} data-id="element-5201">
              Chiudi
            </Button>
            {isPrivateCitizen && privateCitizenName.trim() && <Button variant="flat" color="primary" onClick={() => {
            const newParticipant = {
              id: Date.now(),
              name: privateCitizenName.trim(),
              company: 'Privato cittadino',
              status: 'Iscritto'
            };
            setEnrolledWorkers([...enrolledWorkers, newParticipant]);
            setPrivateCitizenName('');
          }} startContent={<UserPlus className="h-4 w-4" data-id="element-5203" />} data-id="element-5202">
                Aggiungi privato
              </Button>}
            <Button onClick={() => {
            // Save participants logic here
            setIsParticipantModalOpen(false);
            addNotification('success', 'Partecipanti aggiornati', 'Lista partecipanti salvata con successo.');
          }} data-id="element-5204">
              Salva partecipanti
            </Button>
          </div>
        </div>
      </Modal>

      {/* Practice Enrollment Modal */}
      <Modal isOpen={isPracticeModalOpen} onClose={() => setIsPracticeModalOpen(false)} title="Pratica iscrizione" size="lg" data-id="element-5205">
        <AnimatePresence mode="wait" data-id="element-5206">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-5207">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-5208">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-5209" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-5210">
                Pratica iscrizione creata
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-5211">
                Stato: In valutazione
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-5212">
              <div className="space-y-6" data-id="element-5213">
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex items-start gap-3" data-id="element-5214">
                  <FileText className="h-5 w-5 text-blue-600 mt-0.5" data-id="element-5215" />
                  <div data-id="element-5216">
                    <p className="text-sm text-blue-800 font-medium" data-id="element-5217">
                      Nuova pratica iscrizione
                    </p>
                    <p className="text-xs text-blue-600 mt-1" data-id="element-5218">
                      La pratica verrà creata con stato "In valutazione".
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5219">
                  <Select label="Corso" value={formData.courseId || ''} onChange={e => setFormData({
                ...formData,
                courseId: e.target.value
              })} options={activeCourses.map(c => ({
                value: String(c.id),
                label: `${c.code} - ${c.title}`
              }))} required data-id="element-5220" />
                  <Select label="Azienda" value={formData.company || ''} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} options={mockData.getClients().map(c => ({
                value: c.name,
                label: c.name
              }))} required data-id="element-5221" />
                  <Input label="Nome partecipante" value={formData.participantName || ''} onChange={e => setFormData({
                ...formData,
                participantName: e.target.value
              })} placeholder="" required data-id="element-5222" />
                  <Input label="Email partecipante" type="email" value={formData.participantEmail || ''} onChange={e => setFormData({
                ...formData,
                participantEmail: e.target.value
              })} placeholder="" required data-id="element-5223" />
                  <div className="col-span-full" data-id="element-5224">
                    <Textarea label="Note" value={formData.notes || ''} onChange={e => setFormData({
                  ...formData,
                  notes: e.target.value
                })} placeholder="" data-id="element-5225" />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-5226">
                  <Button variant="flat" color="default" onClick={() => setIsPracticeModalOpen(false)} data-id="element-5227">
                    Annulla
                  </Button>
                  <Button onClick={handleSavePractice} startContent={<Save className="h-4 w-4" data-id="element-5229" />} data-id="element-5228">
                    Crea pratica
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-5230">
        <AnimatePresence mode="wait" data-id="element-5231">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-5232">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-5233">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-5234" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-5235">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-5236">
              <div className="space-y-6" data-id="element-5237">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-5238">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-5239" />
                  <p className="text-sm text-red-700" data-id="element-5240">
                    Attenzione: questa azione è irreversibile. Il corso verrà
                    eliminato definitivamente.
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-5241">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-5242">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} data-id="element-5243">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}